import * as React from 'react';

import {deps, inject, observer, StatefulComponent} from '../lib/component';
import {classes, style} from 'typestyle';
import {ResponsiveImage} from '../components/responsive-image';
import {colorBackground, colorBlack, colorGold, colorSlate, colorWhite} from '../theme/color';
import ticket1x from '../static/golden-ticket-visual-1x.png';
import ticket2x from '../static/golden-ticket-visual-2x.png';
import {em, important, percent, px, rem, url, viewWidth} from 'csx';
import {
  center,
  centerCenter,
  endJustified,
  horizontal,
  horizontallySpaced,
  startJustified,
  vertical,
  verticallySpaced
} from 'csstips';
import {mediaMobileOnly, mediaTablet, mediaTabletLandscape} from '../theme/media';
import {ButtonPill} from '../components/button-pill';
import {TextNoOrphan} from '../components/text-no-orphan';
import {CopyBox} from '../components/copy-box';
import FullWidthText from '../components/full-width-text';
import {GOLDEN_TICKET_KEY, PREFERRED_GENRE} from '../constants';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import gql from 'graphql-tag';
import maybe from '../lib/maybe';
import DropdownSelect from '../components/dropdown-select';
import {Modal, ModalChildStyle} from '../components/modal';
import GoogleSignIn from '../components/user/google-sign-in';
import {Logo} from '../components/logo';
import {SVGIcon} from '../components/svg-icon';
import GradientCover from '../components/gradient-cover';
import getCurrentDomain from '../lib/getCurrentDomain';

const PrismicDOM = require('prismic-dom');

type Props = {};
type State = {
  genre1: string;
  genre2: string;
  genre3: string;
  accept: boolean;
};

type Item = {
  label: string;
  value: string;
}

type Data = {
  genres: {
    results: Array<Item>
  }
}

type CombinedProps = Props & InjectedGraphQLProps<Data>

@inject(deps) @observer
class GoldenTicketPage extends StatefulComponent<CombinedProps, State> {
  state = {
    genre1: '',
    genre2: '',
    genre3: '',
    accept: false,
  };

  render() {
    const {data} = this.props;
    const {genre1, genre2, genre3, accept} = this.state;
    const {handleClickLink} = this.props.controller.ui;
    const {preloadContent} = this.props.controller.image;
    const {user, referralURL, channel} = this.props.model.user;
    const model = this.props.model.page.goldenTicket;

    const genres = maybe(
      () => data.genres!.results,
      []
    );

    return (
      <ResponsiveImage
        asBackground={true}
        preload={preloadContent}
        className={GoldenTicketPage.styles.background}
        identity={model.background_image}
      >
        <h1 className={GoldenTicketPage.styles.header}>
          {!user && <TextNoOrphan value={model.header} />}
          {!!user && !!channel && <FullWidthText>{channel.name}</FullWidthText>}
        </h1>
        {!!user && !!channel && (
          <p className={GoldenTicketPage.styles.text}>
            {model.referral_text}
          </p>
        )}
        {!!user && !!channel && (
          <CopyBox
            className={GoldenTicketPage.styles.copyBox}
            content={referralURL}
          />
        )}
        <div className={GoldenTicketPage.styles.visual} />
        {!user && (
          <div className={GoldenTicketPage.styles.content}>
            <h2 className={GoldenTicketPage.styles.subheading}><TextNoOrphan value={model.sub_header}/></h2>
            <p className={GoldenTicketPage.styles.copy}>{model.cta_landing_copy}</p>
            <div className={GoldenTicketPage.styles.genres}>
              <DropdownSelect
                placeholder={model.cta_genre_1_placeholder}
                value={genre1}
                options={genres}
                renderLabel={GoldenTicketPage.renderGenreLabel.bind(null, 1)}
                onChange={this.selectGenre1}
              />
              {!!genre1 && (
                <DropdownSelect
                  placeholder={model.cta_genre_2_placeholder}
                  value={genre2}
                  options={genres.filter(g => g.value !== genre1)}
                  renderLabel={GoldenTicketPage.renderGenreLabel.bind(null, 2)}
                  onChange={this.selectGenre2}
                />
              )}
            </div>
            <span className={classes(
              GoldenTicketPage.styles.buttonContainer,
              !genre1
                ? GoldenTicketPage.styles.buttonContainerHidden
                : GoldenTicketPage.styles.buttonContainerVisible
            )}>
              <ButtonPill
                className={GoldenTicketPage.styles.button}
                disabled={!genre1}
                muted={!genre1}
                bouncing={!!genre1}
                onClick={this.handleSignUp}
              >{model.cta_landing_button}</ButtonPill>
            </span>
          </div>
        )}
        {!!user && (
          <div className={GoldenTicketPage.styles.content}>
            <p className={GoldenTicketPage.styles.text}>{model.cta_complete_copy}</p>
            <ButtonPill
              className={GoldenTicketPage.styles.button}
              primary={true}
              bouncing={true}
              onClick={handleClickLink}
              href={model.cta_complete_url}
            >{model.cta_complete_button}</ButtonPill>
          </div>
        )}
        {!user && (
          <Modal show={accept} onRequestClose={this.closeSignUp}>
            <div className={GoldenTicketPage.styles.modal}>
              <GradientCover className={GoldenTicketPage.styles.modalCover} gradientClassName={GoldenTicketPage.styles.modalCoverGradient}/>
              <h3>{model.modal_title}</h3>
              <div className={GoldenTicketPage.styles.contentCopy} dangerouslySetInnerHTML={{__html: model.loading ? null : PrismicDOM.RichText.asHtml(model.modal_copy) }} />
              <GoogleSignIn />
              <div className={GoldenTicketPage.styles.mandate}>{model.modal_caveat}</div>
              <div className={GoldenTicketPage.styles.legals}>
                <a onClick={this.handleClickTermsConditions} href={'/terms-and-conditions'}>{GoldenTicketPage.i18n.termsConditions}</a>
                <a onClick={this.handleClickPrivacyPolicy} href={'/privacy-policy'}>{GoldenTicketPage.i18n.privacyPolicy}</a>
              </div>
              <div className={GoldenTicketPage.styles.authority}>
                <Logo/>
                <SVGIcon.YouTubeCertified size={32}/>
              </div>
            </div>
          </Modal>
        )}
      </ResponsiveImage>
    )
  }

  selectGenre1 = (genre1: string) => {
    this.setState({genre1}, this.createGenreCookie)
  };

  selectGenre2 = (genre2: string) => {
    this.setState({genre2}, this.createGenreCookie);
  };

  selectGenre3 = (genre3: string) => {
    this.setState({genre3}, this.createGenreCookie);
  };

  private createGenreCookie = () => {
    const {genre1, genre2, genre3} = this.state;
    const genre = [genre1, genre2, genre3].filter(Boolean).join(',');

    const expiry = genre?'':`; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    const domain = '.' + getCurrentDomain();
    
    document.cookie = `${PREFERRED_GENRE}=${genre}${expiry};domain=${domain};path=/`;
  };

  handleSignUp = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    if (!this.state.genre1)
      return;

    this.setState({accept: true});
  };

  closeSignUp = () => this.setState({accept: false});

  handleClickTermsConditions = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.controller.onboarding.showTermsConditionsModal();
  };

  handleClickPrivacyPolicy = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.controller.onboarding.showPrivacyPolicyModal();
  };

  componentWillMount() {
    this.props.controller.ui.setChrome(false);
  }

  componentDidMount() {
    document.cookie = `${GOLDEN_TICKET_KEY}=1`;
  }

  componentWillUnmount() {
    this.props.controller.ui.setChrome(true);
    this.props.controller.ui.setOpenGraph();
  }

  static renderGenreLabel = (index: number, label: string) => `Preferred genre ${index}: ${label}`;

  static i18n = {
    termsConditions: 'Terms & Conditions',
    privacyPolicy: 'Privacy Policy'
  };

  static styles = {
    background: style(
      {
        ...vertical,
        ...center,
        ...startJustified,
        position: 'fixed',
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
        padding: 0,
        overflow: 'hidden',
        backgroundColor: colorBlack.toString(),
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      },
      mediaMobileOnly({
        padding: rem(2),
      }),
      mediaTablet({
        padding: rem(5),
      })
    ),
    modal: style({
      ...ModalChildStyle,
      ...center,
      width: px(420),
      maxWidth: 'calc(100vw - 4rem)',
      background: colorSlate.darken(0.075).toString(),
      color: colorWhite.toString(),
      position: 'relative',
      $nest: {
        '& p': {
          zIndex: 42,
          margin: '0 0 1.5em'
        },
        '& strong': {
          color: colorGold.toString(),
          fontWeight: 600,
        },
        '& h3': {
          zIndex: 42,
          textAlign: 'left',
          width: percent(100),
          color: colorGold.toString(),
          margin: '0 0 2rem 0',
          fontSize: rem(2)
        }
      }
    }),
    modalCover: style({
      borderRadius: rem(1),
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
    }),
    modalCoverGradient: style({
      background: `radial-gradient(
  circle at 94% 10%,
  ${colorGold.fade(0.12).toString()}, 
  ${colorBlack.fade(0.35).toString()} 68%,
  ${colorGold.darken(0.05).fade(0.05).toString()}
)`
    }),
    header: style(
      {
        ...vertical,
        ...center,
        ...endJustified,
        flexShrink: 0,
        margin: 0,
        color: colorWhite.toString(),
        textAlign: 'center',
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: viewWidth(11),
        minHeight: px(120),
      }),
      mediaTablet({
        width: px(570),
        fontSize: rem(3.75),
      }),
      mediaTabletLandscape({
        fontSize: important('7vh'),
      }),
    ),
    visual: style(
      {
        flex: 2,
        width: percent(100),
        maxWidth: px(420),
        backgroundSize: 'contain',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        margin: '1rem 0 2rem',
      },
      mediaMobileOnly({
        backgroundImage: url(ticket1x),
      }),
      mediaTablet({
        backgroundImage: url(ticket2x),
      })
    ),
    copyBox: style({
      maxWidth: percent(100),
    }),
    content: style({
      ...vertical,
      ...verticallySpaced(16),
      ...center,
      ...endJustified,
      flexShrink: 0,
      maxWidth: rem(36)
    }),
    contentCopy: style({
      zIndex: 42,
    }),
    genres: style(
      {
        flexShrink: 0,
      },
      mediaMobileOnly({
        ...vertical,
        ...center,
        ...verticallySpaced(8)
      }),
      mediaTablet({
        ...horizontal,
        ...center,
        ...horizontallySpaced(8),
      })
    ),
    button: style(
      {
        transition: 'margin-top 0.150s ease-in'
      },
      mediaMobileOnly({
        fontSize: rem(1.25)
      }),
      mediaTablet({
        fontSize: rem(1.5)
      }),
      mediaTabletLandscape({
        fontSize: important('1.25rem')
      })
    ),
    buttonContainer: style({
      flexShrink: 0,
      paddingTop: rem(1),
      overflow: 'hidden',
      position: 'relative',
      transition: 'height 0.125s ease-in'
    }),
    buttonContainerHidden: style({
      height: 0,
    }),
    buttonContainerVisible: style(
      mediaMobileOnly({
        height: rem(4)
      }),
      mediaTablet({
        height: rem(5)
      })
    ),
    text: style(
      {
        margin: '1rem 0',
        textAlign: 'center',
        color: colorWhite.toString(),
        lineHeight: em(1.375),
        fontWeight: 200,
        maxWidth: rem(50),
        flexShrink: 0,
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: rem(1.125),
      }),
      mediaTablet({
        width: viewWidth(69),
        fontSize: rem(1.375),
      })
    ),
    subheading: style(
      {
        textAlign: 'center',
        margin: 0,
        color: colorWhite.toString(),
      },
      mediaMobileOnly({
        fontSize: rem(1.5),
      }),
      mediaTablet({
        fontSize: rem(1.875),
      })
    ),
    copy: style(
      {
        margin: 0,
        textAlign: 'center',
        color: colorWhite.toString(),
        lineHeight: em(1.375),
        maxWidth: percent(100),
        fontSize: rem(1),
        flexShrink: 0,
      },
      mediaMobileOnly({
        width: percent(100),
      }),
      mediaTablet({
        width: viewWidth(69),
        padding: '0 0 1rem 0'
      })
    ),
    mandate: style({
      fontSize: rem(0.75)
    }),
    legals: style({
      ...vertical,
      ...verticallySpaced(8),
      ...centerCenter,
      margin: '16px 0 0 0',
      zIndex: 42,
      $nest: {
        '& a': {
          color: colorBackground.fade(0.75).toString(),
          fontSize: px(14),
          lineHeight: em(1.1)
        },
        '& a:hover': {
          color: colorBackground.darken(0.1).fade(0.75).toString(),
        }
      }
    }),
    authority: style({
      ...centerCenter,
      ...horizontal,
      ...horizontallySpaced(30),
      margin: '20px 0 0 0',
      zIndex: 42,
      $nest: {
        '&>svg:first-child': {
          height: px(32),
          width: 'auto',
        },
      }
    })
  };
}

export default graphql(gql`
  query {
    genres: browseTag(options:{type:TAG,source:"hookd",query:"hookd_genre"}) {
      results {
        label: name
        value: slug
      }
    }
  }
`, {
  options: (variables: Props) => ({
    variables,
    // fetchPolicy: 'network-only'
  })
})(GoldenTicketPage);